import {ClusterSummaryView, NewsArticle} from './news';

const MULTI_SOURCE_NAME = 'metagame.news';
const DEFAULT_SOURCE_NAME = 'Unknown';
const DEFAULT_HEADLINE = 'No Title';

function convertRating(raw: number | null | undefined): number {
    return raw ?? 0;
}

function resolveWebsiteName(articleCount: number | null | undefined, sourceName: string | undefined): string {
    return articleCount && articleCount > 1 ? MULTI_SOURCE_NAME : (sourceName || DEFAULT_SOURCE_NAME);
}

/**
 * Standardized data shape for news cards.
 * All data sources (NewsArticle, ClusterSummaryView, UnifiedCard) map into this.
 */
export interface NewsCardData {
    id: number;
    clusterId: number;
    articleUrl: string;
    imageUrl: string | null;
    breakingRating: number;
    websiteName: string;
    articleCount: number | null;
    recentArticleCount: number | null;
    publicationDate: string | null;
    headline: string;
    content: string;
    tagsJson: string | null;
    isInReadLater?: boolean;
}

/**
 * V2 API response shape — returned directly by /v2/news endpoints.
 * The backend already applies all transformations (rating scale, website name, etc).
 */
export interface UnifiedCard {
    id: number;
    cluster_id: number;
    article_url: string;
    image_url: string | null;
    breaking_rating: number;
    website_name: string;
    article_count: number | null;
    recent_article_count: number | null;
    publication_date: string | null;
    headline: string;
    content: string;
    tags_json: string | null;
    is_in_read_later?: boolean;
    like_count: number;
    dislike_count: number;
    is_user_liked?: boolean;
    is_user_disliked?: boolean;
}

/** API filter params sent to V2 endpoints. */
export interface NewsApiFilters {
    region?: string;
    relevance?: string;
}

/** A single filter option returned by GET /v2/news/filters. */
export interface FilterOption {
    label: string;
    value: string;
}

/** Response shape from GET /v2/news/filters. */
export interface FiltersResponse {
    regions: FilterOption[];
    relevance: FilterOption[];
}

/** Convert UI filter state to API params (strips 'all' values). */
export function toApiFilters(filters: { region?: string; relevance?: string }): NewsApiFilters | undefined {
    const region = filters.region || undefined;
    const relevance = filters.relevance && filters.relevance !== 'all' ? filters.relevance : undefined;
    return (region || relevance) ? { region, relevance } : undefined;
}

export function fromUnifiedCard(card: UnifiedCard): NewsCardData {
    return {
        id: card.id,
        clusterId: card.cluster_id,
        articleUrl: card.article_url,
        imageUrl: card.image_url,
        breakingRating: card.breaking_rating,
        websiteName: card.website_name,
        articleCount: card.article_count,
        recentArticleCount: card.recent_article_count,
        publicationDate: card.publication_date,
        headline: card.headline,
        content: card.content,
        tagsJson: card.tags_json,
        isInReadLater: card.is_in_read_later,
    };
}

/**
 * Convert a V1 NewsArticle to UnifiedCard shape.
 * Used for personalized news (V1 path) so it can flow through the same rendering pipeline.
 */
export function newsArticleToUnifiedCard(a: NewsArticle): UnifiedCard {
    return {
        id: a.id,
        cluster_id: a.cluster_id,
        article_url: a.article_url || '',
        image_url: a.image_url,
        breaking_rating: convertRating(a.breaking_rating),
        website_name: resolveWebsiteName(a.article_count, a.website_name),
        article_count: a.article_count,
        recent_article_count: a.recent_article_count,
        publication_date: a.publication_date,
        headline: a.breaking_headline || a.headline || a.title || DEFAULT_HEADLINE,
        content: a.cluster_summary || a.content || '',
        tags_json: a.tags_json,
        is_in_read_later: a.is_in_read_later,
        like_count: 0,
        dislike_count: 0,
    };
}

// Legacy mappers — kept for pages that still use V1 APIs (e.g. NewsDetail, TrendingNews sidebar)
export function fromNewsArticle(article: NewsArticle): NewsCardData {
    return {
        id: article.id,
        clusterId: article.cluster_id,
        articleUrl: article.article_url || '',
        imageUrl: article.image_url,
        breakingRating: convertRating(article.breaking_rating),
        websiteName: resolveWebsiteName(article.article_count, article.website_name),
        articleCount: article.article_count,
        recentArticleCount: article.recent_article_count,
        publicationDate: article.publication_date,
        headline: article.breaking_headline || article.headline || article.title || DEFAULT_HEADLINE,
        content: article.cluster_summary || article.content || '',
        tagsJson: article.tags_json,
        isInReadLater: article.is_in_read_later,
    };
}

export function fromClusterSummary(cluster: ClusterSummaryView): NewsCardData {
    return {
        id: cluster.root_article_id,
        clusterId: cluster.cluster_id,
        articleUrl: (cluster as any).article_url || '',
        imageUrl: cluster.cluster_image,
        breakingRating: convertRating(cluster.last_breaking_rating || cluster.root_breaking_rating),
        websiteName: resolveWebsiteName(cluster.num_related_articles, cluster.website_name),
        articleCount: cluster.num_related_articles,
        recentArticleCount: null,
        publicationDate: cluster.last_breaking_update_datetime || cluster.last_update_datetime || cluster.root_pubdate,
        headline: cluster.last_breaking_headline || cluster.cluster_headline,
        content: cluster.cluster_summary || '',
        tagsJson: cluster.tags_json,
    };
}
